import React, { useState, useEffect } from 'react';
import { useDualWriteStats } from './useDualWriteStats';
import { StatCard } from './StatCard';
import { RowsTable } from './RowsTable';
import { RecentErrors } from './RecentErrors';
import { JsonSyntax } from './JsonSyntax';
import { ErrorDetailView } from './ErrorDetailView';
import type { DualWriteEvent } from './types';

// ── Types ──────────────────────────────────────────────────────

type DrillDown =
  | null
  | { section: 'feed'; filter: 'write' | 'created' | 'updated' | 'skip' | 'error' | 'warning' }
  | { section: 'rows'; filter: 'all' | 'bridged' | 'unbridged' };

/** Check if a drill-down matches a specific card */
function isDrillActive(drill: DrillDown, section: 'feed' | 'rows', filter: string): boolean {
  return drill !== null && drill.section === section && drill.filter === filter;
}

interface WarningStats { active: number; suppressed: number; }

export function DashboardTab({ events, connected, warningStats }: { events: DualWriteEvent[]; connected: boolean; warningStats: WarningStats }) {
  const [drillDown, setDrillDown] = useState<DrillDown>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  // Scroll refs via getElementById (avoids useRef TS issues in this project's config)

  const toggleDrill = (next: NonNullable<DrillDown>) => {
    const isActive = drillDown !== null && drillDown.section === next.section && drillDown.filter === next.filter;
    setDrillDown(isActive ? null : next);
  };

  // Scroll to the relevant section when drill-down changes
  useEffect(() => {
    if (!drillDown) return;
    const target = document.getElementById(drillDown.section === 'feed' ? 'feed-section' : 'rows-section');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [drillDown]);

  const { data, isLoading, error } = useDualWriteStats({ refetchInterval: 30_000 });

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-icon-subtle">Loading stats...</div>;
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-border-red-light bg-bg-red-light p-6 text-content-danger">
        Failed to load dual-write stats. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  const { metrics, coverage, instanceStartedAt } = data;

  // Derive feed filter from drill-down
  const feedFilter = drillDown?.section === 'feed' ? drillDown.filter : null;
  const filteredEvents = !feedFilter
    ? events
    : events.filter((evt) => {
        if (feedFilter === 'write') return evt.type === 'write';
        if (feedFilter === 'created') return evt.type === 'write' && evt.status === 'created';
        if (feedFilter === 'updated') return evt.type === 'write' && evt.status === 'updated';
        if (feedFilter === 'skip') return evt.type === 'skip';
        if (feedFilter === 'error') return evt.type === 'error';
        if (feedFilter === 'warning') return evt.type === 'warning';
        return true;
      });

  // Derive rows filter from drill-down
  const rowsFilter = drillDown?.section === 'rows' ? drillDown.filter : undefined;

  return (
    <div className="space-y-6">
      {/* Clear filter breadcrumb */}
      {drillDown && (
        <div className="flex items-center gap-2 text-sm text-blueberry-700 bg-blueberry-50 border border-blueberry-200 rounded-lg px-3 py-2">
          <span>
            Filtered: <span className="font-medium">{drillDown.section} / {drillDown.filter}</span>
          </span>
          <button
            onClick={() => setDrillDown(null)}
            className="ml-auto text-xs text-blueberry-600 hover:text-blueberry-800 font-medium"
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <p className="text-sm text-icon-subtle">
          Instance started: {new Date(instanceStartedAt).toLocaleString()}
        </p>
        <span className={`inline-flex items-center gap-1.5 text-xs ${connected ? 'text-content-success' : 'text-icon-subtle'}`}>
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${connected ? 'bg-matcha-500 animate-pulse' : 'bg-nightshade-300'}`} />
          {connected ? 'Live' : 'Connecting...'}
        </span>
      </div>

      <div>
        <h3 className="text-sm font-medium text-content-tertiary mb-3">
          Bridge Metrics
          <span className="ml-2 text-xs font-normal text-icon-subtle">(cumulative, persisted across deploys)</span>
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          <StatCard label="Writes" value={metrics.writes} onClick={() => toggleDrill({ section: 'feed', filter: 'write' })} active={isDrillActive(drillDown, 'feed', 'write')} />
          <StatCard label="Creates" value={metrics.creates} onClick={() => toggleDrill({ section: 'feed', filter: 'created' })} active={isDrillActive(drillDown, 'feed', 'created')} />
          <StatCard label="Updates" value={metrics.updates} onClick={() => toggleDrill({ section: 'feed', filter: 'updated' })} active={isDrillActive(drillDown, 'feed', 'updated')} />
          <StatCard label="Skips" value={metrics.skips} onClick={() => toggleDrill({ section: 'feed', filter: 'skip' })} active={isDrillActive(drillDown, 'feed', 'skip')} />
          <StatCard label="Warnings" value={warningStats.active} subtitle={warningStats.suppressed > 0 ? `+${warningStats.suppressed} suppressed` : undefined} accent={warningStats.active > 0 ? 'warning' : undefined} onClick={() => toggleDrill({ section: 'feed', filter: 'warning' })} active={isDrillActive(drillDown, 'feed', 'warning')} />
          <StatCard label="Errors" value={metrics.errors} accent={metrics.errors > 0 ? 'error' : undefined} onClick={() => toggleDrill({ section: 'feed', filter: 'error' })} active={isDrillActive(drillDown, 'feed', 'error')} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-content-tertiary mb-3">Coverage</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total Legacy Rows" value={coverage.totalLegacy.toLocaleString()} onClick={() => toggleDrill({ section: 'rows', filter: 'all' })} active={isDrillActive(drillDown, 'rows', 'all')} />
          <StatCard label="Bridged" value={coverage.bridgedCount.toLocaleString()} onClick={() => toggleDrill({ section: 'rows', filter: 'bridged' })} active={isDrillActive(drillDown, 'rows', 'bridged')} />
          <StatCard label="Total V4" value={coverage.totalV4.toLocaleString()} onClick={() => toggleDrill({ section: 'rows', filter: 'all' })} active={isDrillActive(drillDown, 'rows', 'all')} />
          <StatCard label="Coverage %" value={`${coverage.coveragePercent}%`} onClick={() => toggleDrill({ section: 'rows', filter: 'bridged' })} active={isDrillActive(drillDown, 'rows', 'bridged')} />
        </div>
      </div>

      {/* Live event feed */}
      <div id="feed-section">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-medium text-content-tertiary">
            Live Feed
            {events.length > 0 && <span className="ml-2 text-xs font-normal text-icon-subtle">({filteredEvents.length}{feedFilter ? ` ${feedFilter}` : ''} of {events.length})</span>}
          </h3>
        </div>
        {filteredEvents.length === 0 ? (
          <div className="text-sm text-icon-subtle py-4">
            {events.length === 0
              ? (connected ? 'Waiting for bridge activity...' : 'Connecting to event stream...')
              : `No ${feedFilter} events.`}
          </div>
        ) : (
          <div className="rounded-lg border border-border-secondary overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-bg-secondary text-left text-icon-subtle uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-1.5">Time</th>
                  <th className="px-3 py-1.5">Type</th>
                  <th className="px-3 py-1.5">Shape</th>
                  <th className="px-3 py-1.5">Kind</th>
                  <th className="px-3 py-1.5">Legacy ID</th>
                  <th className="px-3 py-1.5">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-tertiary">
                {filteredEvents.map((evt, i) => {
                  const eventKey = `${evt.timestamp}-${evt.legacyId}-${evt.type}-${i}`;
                  const isExpanded = expandedEvent === eventKey;
                  const hasDetail = evt.type === 'error' || evt.type === 'warning';
                  return (
                    <React.Fragment key={eventKey}>
                      <tr
                        className={`${
                          evt.type === 'error' ? 'bg-bg-red-light' :
                          evt.type === 'warning' ? 'bg-tangerine-50' :
                          evt.type === 'skip' ? 'bg-bg-secondary' :
                          i === 0 ? 'bg-bg-green-light' : ''
                        } ${hasDetail ? 'cursor-pointer' : ''}`}
                        onClick={hasDetail ? () => setExpandedEvent(isExpanded ? null : eventKey) : undefined}
                      >
                        <td className="px-3 py-1.5 font-mono text-icon-subtle">
                          {new Date(evt.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                            evt.type === 'write' ? 'bg-bg-green-light text-content-success' :
                            evt.type === 'warning' ? 'bg-tangerine-50 text-tangerine-600' :
                            evt.type === 'skip' ? 'bg-bg-tertiary text-content-tertiary' :
                            'bg-bg-red-light text-content-danger'
                          }`}>
                            {evt.type}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-content-tertiary">{evt.shape}</td>
                        <td className="px-3 py-1.5">{evt.blueprintKind ?? '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-content-tertiary">{evt.legacyId}</td>
                        <td className="px-3 py-1.5 text-icon-subtle max-w-[200px]">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">
                              {evt.type === 'write' ? `${evt.status} → ${evt.artifactId?.slice(0, 8)}` :
                               hasDetail ? <>{evt.error} <span className="text-icon-subtle">{isExpanded ? '▾' : '▸'}</span></> :
                               evt.externalId ?? '—'}
                            </span>
                            {hasDetail && (
                              <button
                                title="Copy error as JSON"
                                className="shrink-0 text-icon-subtle hover:text-content-tertiary transition-colors"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  const json = JSON.stringify({
                                    error: evt.error,
                                    errorCode: evt.errorCode,
                                    errorDetails: evt.errorDetails,
                                    shape: evt.shape,
                                    blueprintKind: evt.blueprintKind,
                                    legacyId: evt.legacyId,
                                    externalId: evt.externalId,
                                    payload: evt.payload,
                                    timestamp: evt.timestamp,
                                  }, null, 2);
                                  void navigator.clipboard.writeText(json);
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                                  <path d="M5.5 3.5A1.5 1.5 0 0 1 7 2h2.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 1 .439 1.061V9.5A1.5 1.5 0 0 1 12 11V8.621a3 3 0 0 0-.879-2.121L9 4.379A3 3 0 0 0 6.879 3.5H5.5Z" />
                                  <path d="M4 5a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 4 14h5a1.5 1.5 0 0 0 1.5-1.5V8.621a1.5 1.5 0 0 0-.44-1.06L7.94 5.439A1.5 1.5 0 0 0 6.878 5H4Z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={`${evt.type === 'error' ? 'bg-bg-red-light/50' : 'bg-tangerine-50/50'}`}>
                          <td colSpan={6} className="px-3 py-3">
                            <div className="space-y-2">
                              {evt.errorCode && (
                                <div className="text-xs">
                                  <span className={`font-medium ${evt.type === 'error' ? 'text-content-danger' : 'text-tangerine-600'}`}>{evt.type === 'error' ? 'Rejection' : 'Warning'}: </span>
                                  <span className={`font-mono ${evt.type === 'error' ? 'text-content-danger' : 'text-tangerine-500'}`}>{evt.errorCode}</span>
                                  <span className="text-content-tertiary"> — {evt.error}</span>
                                </div>
                              )}
                              {evt.errorDetails && Object.keys(evt.errorDetails).length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-content-tertiary mb-1">{evt.type === 'error' ? 'Error Details' : 'Missing Fields'}</div>
                                  <ErrorDetailView errorCode={evt.errorCode} details={evt.errorDetails} />
                                </div>
                              )}
                              {evt.payload && (
                                <div>
                                  <div className="text-xs font-medium text-content-tertiary mb-1">Payload</div>
                                  <JsonSyntax data={evt.payload} />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent errors — persists across refresh via server buffer */}
      <RecentErrors events={events} />

      <div id="rows-section">
        <RowsTable filter={rowsFilter === 'bridged' ? 'bridged' : rowsFilter === 'unbridged' ? 'unbridged' : undefined} />
      </div>
    </div>
  );
}
